export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

/** Pages that work without a session. */
export const PUBLIC_PATHS = ['/login', '/signup', '/forgot-password', '/reset-password', '/confirm-email', '/terms', '/privacy', '/imprint', '/invite/'];
const isPublicPath = (p: string) => PUBLIC_PATHS.some((x) => p === x || (x.endsWith('/') && p.startsWith(x)));

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
  }
  const res = await fetch(url, init);
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    if (res.status === 401 && !url.endsWith('/api/login') && !isPublicPath(location.pathname)) {
      location.href = '/login?return=' + encodeURIComponent(location.pathname + location.search + location.hash);
    }
    if ((data as any)?.code === 'password_change_required' && location.pathname !== '/') {
      location.href = '/';
    }
    throw new ApiError(res.status, (data as any)?.error ?? res.statusText, (data as any)?.code);
  }
  return data as T;
}

function safeJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T = any>(url: string) => request<T>('GET', url),
  post: <T = any>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  patch: <T = any>(url: string, body: unknown) => request<T>('PATCH', url, body),
  put: <T = any>(url: string, body: unknown) => request<T>('PUT', url, body),
  del: <T = any>(url: string) => request<T>('DELETE', url),
};

export function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
}
